import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Work } from "@/mocks/works";

export default function HeroSlideshow({ slides }: { slides: Work[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const go = (dir: number) => {
    setIndex((prev) => (prev + dir + slides.length) % slides.length);
  };

  return (
    <section className="relative w-full h-[440px] md:h-[620px] overflow-hidden bg-primary-950">
      {slides.map((work, i) => (
        <div
          key={work.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={work.cover}
            alt={`${work.title} 焦点海报`}
            title={`${work.title} - 希岛爱理影视作品`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20" />
        </div>
      ))}

      <div className="relative z-10 h-full w-full px-4 md:px-8 flex items-end">
        <div className="pb-16 md:pb-24 max-w-2xl">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-semibold mb-4">
            <i className="ri-fire-line" />
            重点推荐
          </span>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 leading-tight animate-fade-up">
            希岛爱理影视作品介绍与资讯平台
          </h1>
          <p className="mt-4 text-sm md:text-base text-background-200 leading-relaxed max-w-xl line-clamp-3">
            {slides[index].synopsis}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/works/${slides[index].id}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看作品详情
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/works"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50/10 border border-background-50/40 text-background-50 text-sm font-medium hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              浏览全部作品
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="上一个"
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/20 border border-background-50/40 text-background-50 hover:bg-background-50/30 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="下一个"
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/20 border border-background-50/40 text-background-50 hover:bg-background-50/30 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      <div className="absolute bottom-5 md:bottom-7 right-4 md:right-8 z-20 flex items-center gap-2">
        {slides.map((work, i) => (
          <button
            key={work.id}
            type="button"
            aria-label={`切换到 ${work.title}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-accent-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}